import { useEffect, useState } from "react"
import Button from "../../../components/Button"
import Content from "../../../components/Content"
import Header from "../../../components/Header"
import InputText from "../../../components/InputText"
import TextArea from "../../../components/TextArea"
import config from "../../../config"
import { Info } from "lucide-react"
import Modal from "../../../components/Modal"
import { createTeam } from "../../../services/team.services"
import { useAppSelector } from "../../../store/store"
import { toast } from "react-toastify"

const CreateTeamPage = () => {
    const token = useAppSelector(state => state.token)

    const [requesting, setRequesting] = useState<boolean>(false)
    const [slugInfo, setSlugInfo] = useState(false)
    const [logo, setLogo] = useState<File | null>(null)
    const [data, setData] = useState({
        name: "",
        slug: "",
        description: ""
    })
    const onChangeField: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => setData({ ...data, [event.target.name]: event.target.value })

    const onChangeTeamName: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        let name = event.target.value
        // Filtra caracteres no permitidos con el regex
        const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
        name = name
            .split("")
            .filter((char) => regex.test(char))
            .join("");

        // Elimina espacios consecutivos
        name = name.replace(/\s+/g, " ");

        // Genera el slug
        const slug = createSlug(name);
        setData({ ...data, name, slug });
    };

    const createSlug = (text: string): string => {
        // Reemplaza los acentos por letras sin acento
        const accentMap: { [key: string]: string } = {
            á: "a", é: "e", í: "i", ó: "o", ú: "u",
            Á: "A", É: "E", Í: "I", Ó: "O", Ú: "U",
            ñ: "n", Ñ: "N"
        };

        let slug = text
            .split("")
            .map((char) => accentMap[char] || char) // Reemplaza acentos y ñ
            .join("");

        // Reemplaza los espacios por guiones bajos
        slug = slug.replace(/\s+/g, "-");

        // Convierte a minúsculas
        return slug.toLowerCase();
    };

    const onChangeSlug: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
        const regex = /^[a-zA-ZñÑáéíóúÁÉÍÓÚ0-9-_\.]+$/;
        let slug = event.target.value

        if (slug.length === 0 || regex.test(slug)) {
            setData({ ...data, slug })
        }
    }

    const onLogoChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        const logo = event.target.files?.item(0)

        if (logo) {
            setLogo(logo)
        }
    }


    const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault()

        setRequesting(true)
        createTeam({
            name: data.name,
            logo: logo,
            description: data.description,
            slug: data.slug,
            token: token || ""
        })
            .then(() => {
                toast.success("El equipo ha sido creado correctamente")
            })
            .catch((error) => {
                toast.error(error.response.data.message);
            })
            .finally(() => setRequesting(false))
    }

    return (
        <div className="flex flex-col">
            <Header className="bg-[#46464c] flex flex-col gap-5">
                <p className="text-white font-extrabold uppercase text-4xl">Crear Equipo</p>
                <p className="text-white">No uses insultos o doble sentido en el nombre, slug o la descripción de tu equipo.</p>
            </Header>
            <Content className="flex flex-col">
                <form className="flex flex-col gap-5 self-center w-[420px]" onSubmit={handleSubmit}>
                    <label className="self-center flex flex-col items-center justify-center w-[150px] h-[150px] rounded-full bg-gray-100 cursor-pointer">
                        {logo ?
                            <img className="rounded-full min-h-full min-w-full object-cover object-center" src={URL.createObjectURL(logo)} alt="team" />
                            :
                            <>
                                <span className="text-sm text-gray-700 font-semibold uppercase">Logo</span>
                                <span className="text-sm text-gray-700 font-semibold">150x150</span>
                            </>
                        }
                        <input type="file" onChange={onLogoChange} hidden />
                    </label>

                    <InputText
                        onChange={onChangeTeamName}
                        name="name"
                        placeholder="Nombre"
                        value={data.name}
                    />

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center relative">
                            <InputText
                                className="w-full pr-10"
                                onChange={onChangeSlug}
                                name="slug"
                                placeholder="Slug"
                                value={data.slug}
                            />
                            <button type="button" className="absolute right-3" onClick={() => setSlugInfo(true)}><Info className="text-gray-700" /></button>
                            <Modal modalOpen={slugInfo} set={setSlugInfo}>
                                <div className="flex flex-col gap-5 p-5 w-[370px] bg-white rounded">
                                    <p className="text-xl text-zinc-700">¿Slug? ¿Qué es eso?</p>
                                    <p className="text-zinc-700">Diciéndolo en palabras coloquiales, el <b>Slug</b> de tu equipo es el nombre único por el que se va distinguir y el cual se vará plasmado en la url cuando quieran acceder.</p>
                                    <p className="text-zinc-700">El slug nos permite acceder fácilmente al equipo por url o google y es por esto que el nombre y el slug deben estar relacionados.</p>
                                </div>
                            </Modal>
                        </div>
                        {data.slug.length > 0 && <p className="px-3 text-zinc-700 font-semibold text-xs">{config.PAGE_URL}/teams/<span className="text-blue-500 font-bold">{data.slug}</span>/overview</p>}
                    </div>

                    <TextArea
                        onChange={onChangeField}
                        name="description"
                        placeholder="Descripción"
                        value={data.description}
                    ></TextArea>


                    <Button type="submit" disabled={requesting} spinner={requesting}>Crear</Button>
                </form>
            </Content>
        </div>
    )
}

export default CreateTeamPage